import type { ProviderDto, ProviderTestResult, Role } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { ProviderDialog, type CatalogEntry } from '@/components/provider-dialog';
import { RequireTenant } from '@/components/require-tenant';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime, PROVIDER_STATUS_LABELS } from '@/lib/format';
import { usePaged } from '@/lib/queries';

interface Catalog {
  types: CatalogEntry[];
  claudeHostLoginEnabled: boolean;
}

const STATUS_VARIANT = { untested: 'outline', ok: 'secondary', error: 'destructive' } as const;

function ProviderCard({
  provider,
  catalog,
  canEdit,
  onEdit,
  onDelete,
  tenantId,
}: {
  provider: ProviderDto;
  catalog: CatalogEntry[];
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  tenantId: string;
}) {
  const queryClient = useQueryClient();
  const label = catalog.find((c) => c.type === provider.type)?.label ?? provider.type;
  const test = useMutation({
    mutationFn: () => api<ProviderTestResult>(`/providers/${provider.id}/test`, { method: 'POST' }),
    onSuccess: (result) => {
      if (result.ok)
        toast.success(
          `${provider.name}: connected (${result.models.length} models, ${result.latencyMs} ms)`,
        );
      else toast.error(`${provider.name}: ${result.message ?? 'test failed'}`);
      void queryClient.invalidateQueries({ queryKey: ['providers', tenantId] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="min-w-0">
          <CardTitle className="truncate text-base">{provider.name}</CardTitle>
          <CardDescription>{label}</CardDescription>
        </div>
        <Badge variant={STATUS_VARIANT[provider.status]}>
          {PROVIDER_STATUS_LABELS[provider.status]}
        </Badge>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1">
          {provider.baseUrl && (
            <>
              <dt className="text-muted-foreground">Base URL</dt>
              <dd className="truncate font-mono text-xs">{provider.baseUrl}</dd>
            </>
          )}
          <dt className="text-muted-foreground">API key</dt>
          <dd>{provider.apiKey ? `•••• ${provider.apiKey.last4}` : 'None'}</dd>
          <dt className="text-muted-foreground">Default model</dt>
          <dd className="truncate">{provider.defaultModel ?? '—'}</dd>
          <dt className="text-muted-foreground">Models</dt>
          <dd>{provider.models.length > 0 ? provider.models.length : '—'}</dd>
          <dt className="text-muted-foreground">Last test</dt>
          <dd>{provider.lastCheckedAt ? formatDateTime(provider.lastCheckedAt) : 'Never'}</dd>
        </dl>
        {provider.status === 'error' && provider.lastError && (
          <p className="text-destructive text-sm" role="status">
            {provider.lastError}
          </p>
        )}
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => test.mutate()}
              disabled={test.isPending}
              aria-label={`Test ${provider.name}`}
            >
              {test.isPending ? 'Testing…' : 'Test'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
              aria-label={`Edit ${provider.name}`}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={onDelete}
              aria-label={`Delete ${provider.name}`}
            >
              Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LlmSetupContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const queryClient = useQueryClient();
  const canEdit = hasRole(role, 'admin');
  const catalog = useQuery({
    queryKey: ['provider-catalog'],
    queryFn: () => api<Catalog>('/providers/catalog'),
    staleTime: Infinity,
  });
  const providers = usePaged<ProviderDto>(['providers', tenantId], '/providers');
  const [dialog, setDialog] = useState<{ open: boolean; provider?: ProviderDto }>({ open: false });
  const [deleting, setDeleting] = useState<ProviderDto | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/providers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Provider deleted');
      void queryClient.invalidateQueries({ queryKey: ['providers', tenantId] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = providers.data?.pages.flatMap((p) => p.items) ?? [];
  const types = catalog.data?.types ?? [];
  const search = useListSearch(rows, (p) => [
    p.name,
    p.type,
    types.find((c) => c.type === p.type)?.label,
    p.defaultModel,
  ]);

  return (
    <>
      <PageHeader
        title="LLM setup"
        description="Connect the model providers your agents can use. Keys are encrypted and never shown again."
        actions={
          canEdit && (
            <Button onClick={() => setDialog({ open: true })} disabled={!catalog.data}>
              Add provider
            </Button>
          )
        }
      />
      <ErrorAlert error={providers.error ?? catalog.error} />
      {!providers.isPending && rows.length === 0 ? (
        <EmptyState title="No providers yet">
          {canEdit
            ? 'Add Anthropic, OpenAI, Gemini, a local OpenAI-compatible server such as Ollama, or Local Claude.'
            : 'An admin has not connected any model providers yet.'}
        </EmptyState>
      ) : (
        <>
          <ListSearch
            className="mb-4"
            value={search.query}
            onChange={search.setQuery}
            label="Search providers"
            placeholder="Search by name, provider type or default model"
          />
          <div className="grid gap-4 md:grid-cols-2">
            {search.filtered.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                catalog={types}
                canEdit={canEdit}
                tenantId={tenantId}
                onEdit={() => setDialog({ open: true, provider: p })}
                onDelete={() => setDeleting(p)}
              />
            ))}
          </div>
          {rows.length > 0 && search.filtered.length === 0 && <NoMatches query={search.query} />}
        </>
      )}
      {providers.hasNextPage && (
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => providers.fetchNextPage()}
          disabled={providers.isFetchingNextPage}
        >
          Load more
        </Button>
      )}

      {dialog.open && catalog.data && (
        <ProviderDialog
          key={dialog.provider?.id ?? 'new'}
          tenantId={tenantId}
          catalog={types}
          claudeHostLoginEnabled={catalog.data.claudeHostLoginEnabled}
          provider={dialog.provider}
          open={dialog.open}
          onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        />
      )}

      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The connection and its encrypted API key are removed permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function LlmSetupPage() {
  return (
    <RequireTenant>
      {(tenant) => <LlmSetupContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
